import React, { useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useSupportChat } from '../features/support/hooks/useSupportChat';
import { ChatList } from '../features/chat/components/ChatList';
import { ChatWindow } from '../features/chat/components/ChatWindow';
import { useTranslation } from 'react-i18next';

const SupportChatPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation(['common']);

  const {
    conversations,
    selectedConversation,
    messages,
    loadingMessages,
    selectConversation,
    sendMessage,
    currentTime
  } = useSupportChat();

  const [searchQuery, setSearchQuery] = React.useState('');

  const displayConversations = useMemo(() => {
    return conversations.map(c => {
      const isTeam = c.other_user.is_team;
      const isAdmin = user?.role === 'admin';

      // If I am user, and other is team -> "Support Team"
      // If I am admin, other is user -> Show user name
      // Logic from original file:
      // user!=admin && is_team -> "Đội ngũ hỗ trợ"
      // user==admin -> "Người dùng" (but name is name)

      let displayName = c.other_user.full_name || c.other_user.username;
      if (!isAdmin && isTeam) {
        displayName = t('support.team', { defaultValue: 'Support Team' });
      }

      let avatar = c.other_user.avatar;
      if (!isAdmin && isTeam) {
        // Use a known static or just keep as is if backend sends specific string
        // The original code checked for /static/images/support-team-avatar.png
        // We can enforce it here
      }

      return {
        ...c,
        other_user: {
          ...c.other_user,
          full_name: displayName,
          // We can leave avatar as is or override if needed
        }
      };
    });
  }, [conversations, user?.role, t]);

  const displaySelectedConversation = useMemo(() => {
    if (!selectedConversation) return null;
    return displayConversations.find(c => c.id === selectedConversation.id) || selectedConversation;
  }, [selectedConversation, displayConversations]);

  if (!user) return null;

  return (
    <div className="flex h-[calc(100vh-64px)] bg-white overflow-hidden">
      {/* Sidebar */}
      <div className={`w-full md:w-80 lg:w-96 flex-shrink-0 ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
        <ChatList
          conversations={displayConversations}
          selectedId={selectedConversation?.id}
          onSelect={selectConversation}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          currentTime={currentTime}
        />
      </div>

      {/* Main Chat Window */}
      <div className={`flex-1 flex flex-col min-w-0 ${!selectedConversation ? 'hidden md:flex' : 'flex'}`}>
        <ChatWindow
          conversation={displaySelectedConversation}
          messages={messages}
          currentUser={user}
          onSendMessage={sendMessage}
          loadingMessages={loadingMessages}
        />
      </div>
    </div>
  );
};

export default SupportChatPage;
